// Claim editor — the registry page. Owned by Instance F.
//
// Keeps A's filename so `features/registry.js` (A's file) needs no edit: the
// registry entry {id, label, icon, feature, adminOnly} is the contract, and this
// module is the body behind it. The "Stub" in the name is now only history.
//
// A's registry renders a page with {selectedPractice, features, isAdmin,
// readOnly, onPageChange} and no claim id, so this component answers "which
// claim?" itself:
//
//   * a claim named by the ledger's Edit action (see claimEditorTarget.js) opens
//     straight into the editor;
//   * otherwise a picker lists the claims that CAN be edited, because arriving
//     from the sidebar with nothing selected is a normal way to get here.
//
// The picker deliberately lists only editable claims. The editor refuses a claim
// the payer has already seen — those go through the corrected-claims flow — and
// offering a row that can only answer 409 would be a worse explanation than not
// offering it.
//
// The gate is A's: App.js renders <FeatureOff feature="claim_editor"/> when the
// feature is off, so this never checks it to decide whether to exist.
//
// DO NOT DELETE THIS FILE and do not edit features/registry.js (A's rule, kept
// verbatim from the placeholder this replaced). The registry imports this path
// by name; repointing it there would make one file a six-way merge conflict for
// a change that was never needed. The body below IS the page rather than a
// re-export, because the page is only meaningful with the picker in front of it
// — see the "which claim?" note above.

import React, { useCallback, useEffect, useRef, useState } from "react";
import ClaimEditor from "./ClaimEditor";
import {
  clearClaimEditorTarget,
  readClaimEditorTarget,
  setClaimEditorTarget,
} from "./claimEditorTarget";
import "./ClaimEditor.css";

const API_URL = process.env.REACT_APP_API_URL || "";

// The statuses claim_edit_service.REFUSED_STATUSES will 409. Kept in step with
// the server deliberately rather than derived: the server is authoritative and
// still refuses, this only decides what the picker bothers to offer.
const EDITABLE_STATUSES = ["draft", "pending", "rejected", "error"];

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
}

export default function ClaimEditorStub({
  selectedPractice,
  readOnly = false,
  isAdmin = false,
  features = {},
  onPageChange,
}) {
  const practiceId = selectedPractice?.id;
  const [claimId, setClaimId] = useState(() => readClaimEditorTarget(practiceId));
  // Why the picker is showing when a claim was named: it is not in this practice.
  const [notice, setNotice] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadClaims = useCallback(async () => {
    if (!practiceId || claimId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_URL}/api/practices/${practiceId}/claims` +
          `?status=${EDITABLE_STATUSES.join(",")}&per_page=50`,
        { credentials: "include" },
      );
      if (!response.ok) throw new Error("Could not load claims");
      const data = await response.json();
      setClaims(data.claims || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [practiceId, claimId]);

  useEffect(() => {
    loadClaims();
  }, [loadClaims]);

  // Leaving the practice must not leave a claim from the old one selected. Keyed
  // off a ref rather than firing on mount: this effect used to run on the first
  // render too, which wiped the claim the ledger's Edit action had just named and
  // dropped the biller back to the picker.
  const lastPracticeId = useRef(practiceId);
  useEffect(() => {
    if (lastPracticeId.current === practiceId) return;
    lastPracticeId.current = practiceId;
    clearClaimEditorTarget();
    setClaimId(null);
    setNotice(null);
  }, [practiceId]);

  const open = (id) => {
    setClaimEditorTarget(id, practiceId);
    setNotice(null);
    setClaimId(id);
  };

  const close = () => {
    clearClaimEditorTarget();
    setClaimId(null);
  };

  // A 404 means this practice has no such claim (a stale remembered id, or one from another
  // practice): drop it and offer the picker, rather than a dead "Not found" page.
  const notFound = (missingId) => {
    // Only about the claim on screen: a late answer for an earlier one changes nothing.
    if (missingId !== claimId) return;
    setNotice(`Claim #${claimId} was not found in this practice. Pick a claim below.`);
    clearClaimEditorTarget();
    setClaimId(null);
  };

  if (claimId) {
    return (
      <ClaimEditor
        selectedPractice={selectedPractice}
        claimId={claimId}
        readOnly={readOnly}
        isAdmin={isAdmin}
        features={features}
        onClose={close}
        onNotFound={notFound}
      />
    );
  }

  return (
    <div className="claim-editor-page">
      <div className="ce-header">
        <div>
          <h1 className="page-title">Claim editor</h1>
          <div className="ce-subtitle">
            Every CMS-1500 field on one page, with the 837 that will be sent
            beside it.
          </div>
        </div>
        {onPageChange && (
          <div className="ce-header-actions">
            <button
              className="btn btn-secondary"
              onClick={() => onPageChange("claims")}
            >
              Go to the ledger
            </button>
          </div>
        )}
      </div>

      {notice && <div className="form-message warn">{notice}</div>}
      {error && <div className="form-message error">{error}</div>}

      <section className="ce-section">
        <h2>Pick a claim</h2>
        <p className="ce-hint">
          Only claims that can still be edited are listed. A claim the payer has
          already seen is corrected rather than edited — use “Create corrected
          claim” on the ledger.
        </p>
        {loading && <div className="ce-loading">Loading claims…</div>}
        {!loading && !claims.length && !error && (
          <div className="ce-loading">
            No editable claims for this practice.
          </div>
        )}
        {!loading && claims.length > 0 && (
          <div className="ce-line-scroll">
            <table className="ce-lines ce-picker">
              <thead>
                <tr>
                  <th>Claim</th>
                  <th>Patient</th>
                  <th>Date of service</th>
                  <th>Payer</th>
                  <th>Status</th>
                  <th>Billed</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {claims.map((c) => (
                  <tr key={c.id}>
                    <td>#{c.id}</td>
                    <td>{c.patient_name || "—"}</td>
                    <td>{formatDate(c.date_of_service)}</td>
                    <td>{c.payer_name || "—"}</td>
                    <td>
                      <span className={`ce-status ce-status-${c.claim_status}`}>
                        {c.claim_status}
                      </span>
                    </td>
                    <td className="ce-line-total">
                      {c.billed_amount == null
                        ? "—"
                        : `$${Number(c.billed_amount).toFixed(2)}`}
                    </td>
                    <td>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => open(c.id)}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
